import { Box, Divider, IconButton, Menu, MenuItem, Paper, Stack, Tooltip, Typography } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import CodeIcon from '@mui/icons-material/Code';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import TableChartIcon from '@mui/icons-material/TableChart';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import TitleIcon from '@mui/icons-material/Title';
import PaletteIcon from '@mui/icons-material/Palette';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useState } from 'react';

// Predefined style templates
export const STYLE_TEMPLATES = {
  colors: {
    red: '<span class="text-red">Red text</span>',
    green: '<span class="text-green">Green text</span>',
    blue: '<span class="text-blue">Blue text</span>',
    orange: '<span class="text-orange">Orange text</span>',
    purple: '<span class="text-purple">Purple text</span>',
  },
  highlights: {
    yellow: '<span class="highlight-yellow">Highlighted text</span>',
    blue: '<span class="highlight-blue">Highlighted text</span>',
    green: '<span class="highlight-green">Highlighted text</span>',
  },
  alerts: {
    success: `<div class="alert alert-success">
  <strong>✅ Success</strong><br>
  <span>Operation completed successfully!</span>
</div>`,
    warning: `<div class="alert alert-warning">
  <strong>⚠️ Warning</strong><br>
  <span>Please review these changes.</span>
</div>`,
    error: `<div class="alert alert-error">
  <strong>❌ Error</strong><br>
  <span>An error occurred.</span>
</div>`,
    info: `<div class="alert alert-info">
  <strong>ℹ️ Info</strong><br>
  <span>Additional information here.</span>
</div>`,
  },
  buttons: {
    primary: '<span class="btn btn-primary">Primary Button</span>',
    success: '<span class="btn btn-success">Success Button</span>',
    warning: '<span class="btn btn-warning">Warning Button</span>',
  },
  markdown: {
    heading1: '# Heading 1',
    heading2: '## Heading 2',
    heading3: '### Heading 3',
    bold: '**bold text**',
    italic: '*italic text*',
    link: '[Link text](https://example.com)',
    image: '![Alt text](https://example.com/image.jpg)',
    code: '`inline code`',
    codeBlock: '```javascript\n// Your code here\n```',
    bulletList: '- Item 1\n- Item 2\n- Item 3',
    numberedList: '1. Item 1\n2. Item 2\n3. Item 3',
    quote: '> This is a quote',
    table: '| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Data 1 | Data 2 | Data 3 |',
    hr: '---',
  },
};

interface MarkdownToolbarProps {
  onInsert: (text: string) => void;
  disabled?: boolean;
}

export const MarkdownToolbar: React.FC<MarkdownToolbarProps> = ({ onInsert, disabled }) => {
  const [colorMenuAnchor, setColorMenuAnchor] = useState<null | HTMLElement>(null);
  const [alertMenuAnchor, setAlertMenuAnchor] = useState<null | HTMLElement>(null);
  const [headingMenuAnchor, setHeadingMenuAnchor] = useState<null | HTMLElement>(null);

  const handleInsert = (template: string) => {
    onInsert(template);
    // Close all menus
    setColorMenuAnchor(null);
    setAlertMenuAnchor(null);
    setHeadingMenuAnchor(null);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        backgroundColor: '#f8fafc',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        mb: 2,
      }}
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
        {/* Headings */}
        <Tooltip title="Headings">
          <IconButton
            size="small"
            onClick={(e) => setHeadingMenuAnchor(e.currentTarget)}
            disabled={disabled}
            sx={{ color: 'text.primary' }}
          >
            <TitleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={headingMenuAnchor} open={Boolean(headingMenuAnchor)} onClose={() => setHeadingMenuAnchor(null)}>
          <MenuItem onClick={() => handleInsert(STYLE_TEMPLATES.markdown.heading1)}>
            <Typography variant="h6">Heading 1</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleInsert(STYLE_TEMPLATES.markdown.heading2)}>
            <Typography variant="subtitle1">Heading 2</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleInsert(STYLE_TEMPLATES.markdown.heading3)}>
            <Typography variant="subtitle2">Heading 3</Typography>
          </MenuItem>
        </Menu>

        {/* Basic formatting */}
        <Tooltip title="Bold">
          <IconButton
            size="small"
            onClick={() => handleInsert(STYLE_TEMPLATES.markdown.bold)}
            disabled={disabled}
            sx={{ color: 'text.primary' }}
          >
            <FormatBoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Italic">
          <IconButton
            size="small"
            onClick={() => handleInsert(STYLE_TEMPLATES.markdown.italic)}
            disabled={disabled}
            sx={{ color: 'text.primary' }}
          >
            <FormatItalicIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Colors */}
        <Tooltip title="Text Colors">
          <IconButton
            size="small"
            onClick={(e) => setColorMenuAnchor(e.currentTarget)}
            disabled={disabled}
            sx={{ color: 'text.primary' }}
          >
            <PaletteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={colorMenuAnchor} open={Boolean(colorMenuAnchor)} onClose={() => setColorMenuAnchor(null)}>
          <MenuItem onClick={() => handleInsert(STYLE_TEMPLATES.colors.red)}>
            <Box sx={{ width: 16, height: 16, backgroundColor: 'red', borderRadius: 1, mr: 1 }} />
            <Typography>Red Text</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleInsert(STYLE_TEMPLATES.colors.green)}>
            <Box sx={{ width: 16, height: 16, backgroundColor: 'green', borderRadius: 1, mr: 1 }} />
            <Typography>Green Text</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleInsert(STYLE_TEMPLATES.colors.blue)}>
            <Box sx={{ width: 16, height: 16, backgroundColor: 'blue', borderRadius: 1, mr: 1 }} />
            <Typography>Blue Text</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleInsert(STYLE_TEMPLATES.colors.orange)}>
            <Box sx={{ width: 16, height: 16, backgroundColor: 'orange', borderRadius: 1, mr: 1 }} />
            <Typography>Orange Text</Typography>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleInsert(STYLE_TEMPLATES.highlights.yellow)}>
            <Box sx={{ width: 16, height: 16, backgroundColor: 'yellow', borderRadius: 1, mr: 1 }} />
            <Typography>Yellow Highlight</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleInsert(STYLE_TEMPLATES.highlights.blue)}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#d1ecf1', borderRadius: 1, mr: 1 }} />
            <Typography>Blue Highlight</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleInsert(STYLE_TEMPLATES.highlights.green)}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#d4edda', borderRadius: 1, mr: 1 }} />
            <Typography>Green Highlight</Typography>
          </MenuItem>
        </Menu>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Alerts */}
        <Tooltip title="Alert Boxes">
          <IconButton
            size="small"
            onClick={(e) => setAlertMenuAnchor(e.currentTarget)}
            disabled={disabled}
            sx={{ color: 'text.primary' }}
          >
            <WarningIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={alertMenuAnchor} open={Boolean(alertMenuAnchor)} onClose={() => setAlertMenuAnchor(null)}>
          <MenuItem onClick={() => handleInsert(STYLE_TEMPLATES.alerts.success)}>
            <CheckCircleIcon sx={{ mr: 1, color: '#4caf50' }} fontSize="small" />
            <Typography>Success Alert</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleInsert(STYLE_TEMPLATES.alerts.warning)}>
            <WarningIcon sx={{ mr: 1, color: '#ff9800' }} fontSize="small" />
            <Typography>Warning Alert</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleInsert(STYLE_TEMPLATES.alerts.error)}>
            <ErrorIcon sx={{ mr: 1, color: '#f44336' }} fontSize="small" />
            <Typography>Error Alert</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleInsert(STYLE_TEMPLATES.alerts.info)}>
            <InfoIcon sx={{ mr: 1, color: '#2196f3' }} fontSize="small" />
            <Typography>Info Alert</Typography>
          </MenuItem>
        </Menu>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Lists */}
        <Tooltip title="Bullet List">
          <IconButton
            size="small"
            onClick={() => handleInsert(STYLE_TEMPLATES.markdown.bulletList)}
            disabled={disabled}
            sx={{ color: 'text.primary' }}
          >
            <FormatListBulletedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Numbered List">
          <IconButton
            size="small"
            onClick={() => handleInsert(STYLE_TEMPLATES.markdown.numberedList)}
            disabled={disabled}
            sx={{ color: 'text.primary' }}
          >
            <FormatListNumberedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Code */}
        <Tooltip title="Code Block">
          <IconButton
            size="small"
            onClick={() => handleInsert(STYLE_TEMPLATES.markdown.codeBlock)}
            disabled={disabled}
            sx={{ color: 'text.primary' }}
          >
            <CodeIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Quote */}
        <Tooltip title="Quote">
          <IconButton
            size="small"
            onClick={() => handleInsert(STYLE_TEMPLATES.markdown.quote)}
            disabled={disabled}
            sx={{ color: 'text.primary' }}
          >
            <FormatQuoteIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Link */}
        <Tooltip title="Link">
          <IconButton
            size="small"
            onClick={() => handleInsert(STYLE_TEMPLATES.markdown.link)}
            disabled={disabled}
            sx={{ color: 'text.primary' }}
          >
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Image */}
        <Tooltip title="Image">
          <IconButton
            size="small"
            onClick={() => handleInsert(STYLE_TEMPLATES.markdown.image)}
            disabled={disabled}
            sx={{ color: 'text.primary' }}
          >
            <ImageIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Table */}
        <Tooltip title="Table">
          <IconButton
            size="small"
            onClick={() => handleInsert(STYLE_TEMPLATES.markdown.table)}
            disabled={disabled}
            sx={{ color: 'text.primary' }}
          >
            <TableChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Click buttons to insert formatting. You can also type markdown or HTML directly.
      </Typography>
    </Paper>
  );
};
